import { useState, useRef, ChangeEvent } from 'react';
import StepHeader from './StepHeader';
import { scrapeProduct, type UserProduct } from '@/lib/api';

interface StepUploadProps {
  uploadedImages: string[];
  maxImages: number;
  onUpload: (images: string[], files: File[]) => void;
  platformId: string;
  /** Called when a product page URL scrape succeeds. Passes the extracted
   *  name + description back to the parent so StepDetails can be pre-
   *  populated and skip the auto-analyze step. Optional — /appv2 omits it
   *  and falls back to the analyze-product flow.
   *
   *  As of May 2026 the scrape endpoint also runs a cheap text-only
   *  classifier and returns `productCategory` + `tags` so StepBackground's
   *  "Recommended for you" tab can populate without a separate vision
   *  call. Both are optional — degrade gracefully if classification
   *  failed server-side. */
  onScrapeSuccess?: (
    name: string,
    description: string,
    insights?: { productCategory?: string; tags?: string[] },
  ) => void;
  /** When true, skip the StepHeader. /appv2's Section wrapper provides
   *  its own header (title + description + icon), so the inner one would
   *  be a duplicate. */
  compact?: boolean;
  /** When set with an uploaded image, render the post-upload state as a
   *  slim "thumbnail + filename + replace" row instead of the big
   *  aspect-square preview card. Saves ~400px of vertical when the
   *  upload step lives inside a tight accordion section (e.g. the
   *  Recreate runtime page where the full result is already shown in
   *  the LEFT pane). */
  compactPreview?: boolean;
  /** Optional saved-products library from /api/products. When provided
   *  (even as an empty array), StepUpload renders a two-column layout:
   *  LEFT = library picker grid, RIGHT = URL fetch + manual upload.
   *  When undefined (the default), the original single-column layout
   *  renders — preserves /app and /appv2/recreate behavior. */
  savedProducts?: UserProduct[];
  /** True while the parent is still fetching /api/products. Drives a
   *  loading state in the library pane + keeps the tab defaulted to
   *  "library" (instead of prematurely falling to URL/upload while the
   *  empty in-flight list looks like "no products"). */
  savedProductsLoading?: boolean;
  /** Called when a user picks a saved product from the library. Parent
   *  is responsible for: downloading the image into a File, calling
   *  onUpload, and populating name + description + classification
   *  fields. Async so the parent can show a loading spinner — return
   *  a Promise that resolves when the pick is fully processed. */
  onSavedProductSelect?: (product: UserProduct) => Promise<void> | void;
  /** Currently-selected saved product id (when the active upload came
   *  from a library pick). Drives the selected-tile visual state —
   *  primary-color ring + checkmark badge. Pass `null` when the active
   *  upload is from URL scrape or manual file (no tile is selected). */
  selectedSavedProductId?: string | null;
  /** Multi-product mode (carousel route): an ordered array of all
   *  picked saved-product ids. Each selected tile shows its 1-indexed
   *  position badge instead of a checkmark. Mutually exclusive with
   *  `selectedSavedProductId` — pass one OR the other, not both. */
  selectedSavedProductIds?: string[];
  /** Optional link target shown above the library pane when the saved-
   *  products list is empty. Default: '/products'. Lets us hide the
   *  link by passing null. */
  productsManageHref?: string;
  /** When true (and savedProducts has items), render a TAB switcher
   *  between the Library pane and the URL+Upload pane instead of the
   *  two-column side-by-side layout. Used by /appv2/recreate/:id where
   *  the right pane is too narrow for side-by-side. Default tab is
   *  "library" when products exist; falls back to "upload" otherwise. */
  tabsMode?: boolean;
  /** Optional predicate to disable specific saved-product tiles
   *  (returns `true` → tile is greyed out + non-clickable). Used by
   *  the Fashion route to lock out non-fashion saved products. When
   *  returning true the caller MAY also supply `disabledReason`
   *  below to show a tooltip explaining why. Default: nothing
   *  disabled. */
  isSavedProductDisabled?: (product: UserProduct) => boolean;
  /** Tooltip shown on disabled tiles. Plain string — surfaces in the
   *  native `title` attribute on hover. Caller is responsible for
   *  wording (e.g. "This isn't a fashion item — pick a different
   *  product or upload a garment photo"). */
  disabledReason?: string;
  /** Short ribbon label rendered centered on top of disabled tiles
   *  (e.g. "Not for fashion"). Replaces the prior lock-icon badge
   *  with explicit text so users immediately see WHY a tile is
   *  locked. Defaults to "Not available" when omitted. */
  disabledLabel?: string;
  /** Multi-product mode (carousel) — deselect a saved product that's
   *  already in the selection. Required for multiSelect to work
   *  properly; without it tiles can only toggle ON, not OFF. */
  onSavedProductDeselect?: (product: UserProduct) => void;
  /** Multi-product mode — append a NEW product from URL/file upload
   *  to the parent's product array. When set, takes precedence over
   *  onUpload for the URL+Upload paths (which would otherwise
   *  replace the whole product array). Optional `name` / `description`
   *  pre-populate the per-product metadata when the source carries
   *  them (URL scrape returns name+desc; file uploads pass undefined). */
  onAddProduct?: (image: string, file: File, name?: string, description?: string) => void;
  /** Multi-product mode — remove uploaded product at the given index. */
  onRemoveProduct?: (index: number) => void;
}

/* ------------------------------------------------------------------ */
/* Image resize utility — max 1200px, jpeg 0.85 quality                */
/* ------------------------------------------------------------------ */
function resizeImage(dataURL: string, maxDim = 1200): Promise<string> {
  return new Promise((resolve) => {
    const img = new Image();
    img.onload = () => {
      const canvas = document.createElement('canvas');
      let { width, height } = img;
      if (width > maxDim || height > maxDim) {
        const ratio = Math.min(maxDim / width, maxDim / height);
        width *= ratio;
        height *= ratio;
      }
      canvas.width = Math.round(width);
      canvas.height = Math.round(height);
      const ctx = canvas.getContext('2d');
      if (ctx) {
        ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
      }
      resolve(canvas.toDataURL('image/jpeg', 0.85));
    };
    img.onerror = () => resolve(dataURL);
    img.src = dataURL;
  });
}

export default function StepUpload({
  uploadedImages,
  maxImages,
  onUpload,
  platformId,
  onScrapeSuccess,
  compact,
  compactPreview,
  savedProducts,
  savedProductsLoading = false,
  onSavedProductSelect,
  selectedSavedProductId,
  selectedSavedProductIds,
  productsManageHref = '/products',
  tabsMode = false,
  isSavedProductDisabled,
  disabledReason,
  disabledLabel = 'Not available',
  onSavedProductDeselect,
  onAddProduct,
  onRemoveProduct,
}: StepUploadProps) {
  const [isDragging, setIsDragging] = useState(false);
  const fileRef = useRef<HTMLInputElement>(null);

  // ----- URL-scrape state -----
  // Mirrors the file-upload flow: user pastes a product URL, backend
  // fetches the page + image, and we feed the result into the same
  // onUpload pipeline. Errors are caught and displayed inline so the user
  // can fall back to manual upload below.
  const [productUrl, setProductUrl] = useState('');
  const [scraping, setScraping] = useState(false);
  const [scrapeError, setScrapeError] = useState<string | null>(null);

  // ----- Library-pick state -----
  // Set to the product id while we're downloading the saved product's
  // image + populating state. Disables the rest of the grid so the user
  // can't fire multiple picks at once.
  const [pickingId, setPickingId] = useState<string | null>(null);
  const [pickError, setPickError] = useState<string | null>(null);

  // ----- Library search -----
  // Free-text filter over the saved-products library. Mirrors the My
  // Products page predicate (name + description + category + tags,
  // case-insensitive substring) so search behaves identically across
  // both surfaces. Only surfaced when the library is large enough to
  // scroll (>6 products) — see the input render below.
  const [productSearch, setProductSearch] = useState('');
  const filteredSavedProducts = useMemo(() => {
    const list = savedProducts ?? [];
    const q = productSearch.trim().toLowerCase();
    if (!q) return list;
    return list.filter((p) => {
      const hay = [
        p.name ?? '',
        p.description ?? '',
        p.productCategory ?? '',
        ...(p.tags ?? []),
      ].join(' ').toLowerCase();
      return hay.includes(q);
    });
  }, [savedProducts, productSearch]);

  const isCarousel = platformId === 'instagram-carousel';
  const withLibrary = savedProducts !== undefined;
  // Multi-select mode kicks in whenever the parent allows more than
  // one image (currently only the carousel route). Library picks toggle
  // membership in selectedSavedProductIds; URL/upload paths APPEND to
  // uploadedImages instead of replacing. At-cap = no more adds.
  const multiSelect = maxImages > 1;
  const atCap = uploadedImages.length >= maxImages;
  const useTabs = tabsMode && withLibrary;
  // Active tab state — only relevant when useTabs is true. Default to
  // 'library' when at least one saved product is USABLE in the current
  // route (i.e. not disabled by isSavedProductDisabled), 'upload'
  // otherwise. This handles the empty-library case AND the "library
  // exists but everything's disabled by the route filter" case — e.g.
  // user lands on Fashion with only non-fashion saved products, so we
  // default them straight to URL/upload instead of making them stare
  // at a grid of greyed-out tiles and figure out the tab switch.
  const hasUsableSavedProducts = (savedProducts ?? []).some(
    (p) => !isSavedProductDisabled || !isSavedProductDisabled(p),
  );
  // While the products are still loading, DEFAULT to 'library' so the
  // loader is visible — otherwise the empty in-flight list reads as "no
  // products" and we'd default to URL/upload, then jarringly flip to
  // library once they arrive (the bug this fixes).
  const [activeTab, setActiveTab] = useState<'library' | 'upload'>(
    savedProductsLoading || hasUsableSavedProducts ? 'library' : 'upload',
  );
  // Flip default when the products list arrives async (loading → empty
  // OR populated), OR when the route filter changes such that the
  // previously-usable products become locked out. Skip after the user
  // manually picks a tab (tabUserPickedRef sticky-tracks that). While
  // loading, hold on 'library' (show the loader); only decide upload-vs-
  // library once the fetch has settled.
  const tabUserPickedRef = useRef(false);
  useEffect(() => {
    if (!useTabs || tabUserPickedRef.current) return;
    if (savedProductsLoading) {
      setActiveTab('library');
      return;
    }
    setActiveTab(hasUsableSavedProducts ? 'library' : 'upload');
  }, [useTabs, hasUsableSavedProducts, savedProductsLoading]);
  // In library mode the right column is narrow (~45% of row), so cards
  // drop their `max-w-2xl` cap and the preview shrinks to a fixed
  // ~240px square so the section doesn't grow when an image lands.
  const cardMaxW    = withLibrary ? '' : 'max-w-2xl';
  const previewMaxW = withLibrary ? 'max-w-[240px]' : 'max-w-md';
  const previewMaxH = withLibrary ? 'max-h-[240px]' : 'max-h-[60vh]';
  // In library mode the entire right column is wrapped in a single
  // panel so its bottom edge aligns with the left panel's. The inner
  // cards drop their own chrome (bg/border/padding) — the outer
  // wrapper provides it instead. In non-library mode each card keeps
  // its individual chrome (centered single-column layout).
  const innerCardChrome = withLibrary
    ? '' // chrome lives on the right-column wrapper
    : 'rounded-2xl border-2 border-surface-dark bg-white p-5';
  const innerPreviewChrome = withLibrary
    ? '' // chrome lives on the right-column wrapper
    : 'rounded-2xl bg-white border border-surface-dark/40 p-5';

  /** Convert a base64 string (no data: prefix) to a real File so the rest
   *  of the wizard treats it identically to a manually-uploaded file. */
  const base64ToFile = (b64: string, filename = 'scraped-product.jpg'): File => {
    const byteString = atob(b64);
    const bytes = new Uint8Array(byteString.length);
    for (let i = 0; i < byteString.length; i++) bytes[i] = byteString.charCodeAt(i);
    return new File([bytes], filename, { type: 'image/jpeg' });
  };

  const handleScrapeUrl = async () => {
    const trimmed = productUrl.trim();
    if (!trimmed) {
      setScrapeError('Please paste a product page URL first.');
      return;
    }
    // Multi-mode cap check — block scrape attempts when full.
    if (multiSelect && atCap) {
      setScrapeError(`You can pick up to ${maxImages} products. Remove one to add another.`);
      return;
    }
    setScrapeError(null);
    setScraping(true);
    try {
      const result = await scrapeProduct(trimmed);
      // Build the data-URL preview from the base64 so existing display code
      // (which expects a data: URL in uploadedImages[0]) keeps working.
      const dataUrl = `data:image/jpeg;base64,${result.imageBase64}`;
      // Unique per-scrape filename so downstream caches keyed on
      // file.name (StepSocialFinal's composedKeyRef, StepPreview's
      // generation cache) don't collide across different products
      // scraped via URL. base64ToFile's default name was constant.
      const file = base64ToFile(result.imageBase64, `scraped-${Date.now()}.jpg`);
      // Multi mode: append via onAddProduct so the parent doesn't
      // wipe the existing array. Pass the scraped name + description
      // through so the per-product metadata array gets pre-populated
      // (StepDetails skips analyze for entries that already have
      // both name + description set).
      if (multiSelect && onAddProduct) {
        onAddProduct(dataUrl, file, result.name, result.description);
        // Clear the URL input so the user can paste another product
        // immediately without manually clearing first.
        setProductUrl('');
      } else {
        onUpload([dataUrl], [file]);
      }
      if (onScrapeSuccess) {
        onScrapeSuccess(result.name, result.description, {
          productCategory: result.productCategory,
          tags: result.tags,
        });
      }
    } catch (err) {
      // The backend returns 400 with a clean message ("couldn't find image",
      // "page took too long", etc). Use the message directly so the user
      // sees the specific failure mode rather than a generic error.
      setScrapeError(
        err instanceof Error
          ? err.message
          : 'Could not fetch the product. Please upload the image manually below.',
      );
    } finally {
      setScraping(false);
    }
  };

  // Returns the original File (for backend) plus a resized data URL (for preview)
  const readFile = (file: File): Promise<{ dataURL: string; file: File } | null> => {
    return new Promise((resolve) => {
      if (!file.type.startsWith('image/')) return resolve(null);
      const reader = new FileReader();
      reader.onload = async (e) => {
        const raw = (e.target?.result as string) || null;
        if (!raw) return resolve(null);
        const resized = await resizeImage(raw, 1200);
        resolve({ dataURL: resized, file });
      };
      reader.onerror = () => resolve(null);
      reader.readAsDataURL(file);
    });
  };

  const processFiles = async (files: FileList | null) => {
    if (!files || files.length === 0) return;
    // Multi-mode cap guard — silently no-op when at limit. The drop
    // zone is also visually locked when atCap, so we shouldn't reach
    // here in normal use; this is the defense-in-depth net.
    if (multiSelect && atCap) return;
    const result = await readFile(files[0]);
    if (!result) return;
    // Multi mode: append via onAddProduct so the parent doesn't
    // wipe its existing array. Single mode: replace via onUpload
    // (pre-multi-product behavior preserved).
    if (multiSelect && onAddProduct) {
      onAddProduct(result.dataURL, result.file);
    } else {
      onUpload([result.dataURL], [result.file]);
    }
  };

  const onInputChange = (e: ChangeEvent<HTMLInputElement>) => {
    processFiles(e.target.files);
    e.target.value = '';
  };

  const onDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
    processFiles(e.dataTransfer.files);
  };

  const handleLibraryPick = async (product: UserProduct) => {
    if (!onSavedProductSelect) return;
    if (pickingId) return;
    // Multi-select toggle: if the tile is already in the selection,
    // remove it instead of attempting another add. Synchronous —
    // skips the loading spinner since no network work is required.
    if (multiSelect && selectedSavedProductIds?.includes(product.id)) {
      onSavedProductDeselect?.(product);
      return;
    }
    // Multi-select cap: ignore further adds once we hit maxImages.
    if (multiSelect && atCap) {
      setPickError(`You can pick up to ${maxImages} products. Remove one to add another.`);
      return;
    }
    setPickError(null);
    setPickingId(product.id);
    try {
      await onSavedProductSelect(product);
    } catch (err) {
      setPickError(
        err instanceof Error
          ? err.message
          : 'Could not use that product. Please try another.',
      );
    } finally {
      setPickingId(null);
    }
  };

  return (
    // /app: h-full flex column inside the fixed-viewport wizard panel.
    // /appv2 (compact): no height constraint — content flows in the
    // page's natural scroll.
    <div className={compact ? '' : 'h-full flex flex-col'}>
      {!compact && (
        <StepHeader
          icon="ri-upload-cloud-line"
          title={withLibrary ? 'Product Selection' : 'Upload Your Product'}
          subtitle={
            withLibrary
              ? 'Pick a saved product from your library on the left, or add a new one on the right.'
              : isCarousel
                ? 'Upload a single product photo. It will be used across all 4 carousel frames.'
                : 'Upload a clear photo of your product on a plain or white background for best AI blending results.'
          }
        />
      )}

      {/* Three layout modes:
          - useTabs (recreate page, tight right pane): single column with
            a tab switcher between Library and URL+Upload panes.
          - withLibrary side-by-side (wizard Upload step): LEFT pane is
            the saved-products grid; RIGHT pane is URL+Upload.
          - default (legacy /app): centered single-column flow. */}
      <div className={`${compact ? '' : 'flex-1 min-h-0 overflow-y-auto'} ${
        useTabs
          ? 'flex flex-col gap-3'
          : withLibrary
            ? 'grid grid-cols-1 lg:grid-cols-[minmax(0,55fr)_minmax(0,45fr)] gap-4 pt-2'
            : 'flex flex-col items-center gap-4 pt-2'
      }`}>

        {/* ============ Tab switcher (useTabs only) ============ */}
        {useTabs && (
          <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-1 p-1 rounded-2xl sm:rounded-full bg-surface border border-surface-dark/30 w-full">
            {([
              { id: 'library' as const, label: 'From library', icon: 'ri-shopping-bag-3-line' },
              { id: 'upload'  as const, label: 'URL or upload', icon: 'ri-upload-cloud-2-line' },
            ]).map((tab) => {
              const isActive = activeTab === tab.id;
              return (
                <button
                  key={tab.id}
                  onClick={() => {
                    tabUserPickedRef.current = true;
                    setActiveTab(tab.id);
                  }}
                  className={`
                    flex-1 inline-flex items-center justify-center gap-2
                    px-4 py-2 rounded-full text-sm font-bold
                    transition-all cursor-pointer
                    ${isActive
                      ? 'bg-white text-primary shadow-md shadow-primary/15'
                      : 'text-dark-muted hover:text-dark'}
                  `}
                >
                  <i className={`${tab.icon} text-base`} />
                  {tab.label}
                </button>
              );
            })}
          </div>
        )}

        {/* ============ LEFT COLUMN — saved-products library ============
            Capped at ~3 rows visible; scrolls beyond. Panel sizes to its
            content (was stretching with h-full + flex-1 for bottom-edge
            alignment with the right column; that was making the section
            grow uncontrollably as users added more saved products and
            the entry-point hit visible breakage). When users have many
            products, the inner grid scrolls; right column ends slightly
            below if shorter, but it's a known + acceptable tradeoff.
            In useTabs mode this pane is hidden when activeTab !== 'library'. */}
        {withLibrary && (!useTabs || activeTab === 'library') && (
          <div className="rounded-2xl border-2 border-surface-dark bg-white p-3 sm:p-4 flex flex-col">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 mb-2 sm:mb-3">
              <div className="flex items-center gap-2 min-w-0">
                {/* Icon hidden on mobile so the headline + subheading use the
                    full row width; shown alongside the text at sm+. */}
                <div className="hidden sm:flex w-8 h-8 rounded-lg bg-primary/10 items-center justify-center flex-shrink-0">
                  <i className="ri-shopping-bag-3-line text-primary text-sm" />
                </div>
                <div className="flex-1 min-w-0">
                  <h3 className="font-display font-bold text-dark text-sm leading-tight">
                    Your saved products
                  </h3>
                  {/* Subtext hidden on mobile (the grid is self-explanatory and
                      the space is better spent on product thumbs); shown sm+. */}
                  <p className="hidden sm:block text-[11px] text-dark-muted leading-tight mt-0.5">
                    {savedProducts && savedProducts.length > 0
                      ? 'Click one to use it instantly — we already have its name + description.'
                      : 'Nothing saved yet — add products from URLs first.'}
                  </p>
                </div>
              </div>
              {productsManageHref && (
                <a
                  href={productsManageHref}
                  className="inline-flex items-center gap-1 text-xs font-bold text-primary hover:text-primary-hover whitespace-nowrap"
                  title="Manage saved products"
                >
                  Manage
                  <i className="ri-external-link-line text-xs" />
                </a>
              )}
            </div>

            {savedProductsLoading ? (
              <div className="flex-1 min-h-0">
                <div className="flex items-center gap-2 mb-3 text-xs text-dark-muted">
                  <i className="ri-loader-4-line animate-spin text-sm" />
                  Loading your products…
                </div>
                <div className="grid grid-cols-3 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-2">
                  {Array.from({ length: 6 }).map((_, i) => (
                    <div
                      key={i}
                      className="aspect-square rounded-lg bg-surface-dark/20 animate-pulse"
                    />
                  ))}
                </div>
              </div>
            ) : savedProducts && savedProducts.length > 0 ? (
              <>
                {/* Search — only surfaced once the library is big
                    enough to scroll (>6). Same predicate as the My
                    Products page so filtering behaves identically. */}
                {savedProducts.length > 6 && (
                  <div className="relative mb-3">
                    <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-dark-muted text-sm" />
                    <input
                      type="text"
                      value={productSearch}
                      onChange={(e) => setProductSearch(e.target.value)}
                      placeholder="Search by name, category, or tag…"
                      className="w-full pl-9 pr-9 py-2 rounded-full bg-surface/40 border border-surface-dark/40 text-sm focus:outline-none focus:border-primary"
                    />
                    {productSearch && (
                      <button
                        type="button"
                        onClick={() => setProductSearch('')}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-dark-muted hover:text-dark"
                        title="Clear search"
                      >
                        <i className="ri-close-circle-fill text-sm" />
                      </button>
                    )}
                  </div>
                )}
                {/* Smaller thumbs at wide widths: 3→4→5→6 cols so
                    tiles stay ~150–180px even when the panel fills the
                    full section width (tabs mode in the wizard). The
                    higher col count keeps 2+ rows visible in the
                    max-h cap. max-h sized for ~2 rows of ~180px tile
                    + ~22px label + gap. overflow-y-auto scrolls beyond. */}
                {filteredSavedProducts.length > 0 ? (
                <div className="grid grid-cols-3 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-2 sm:max-h-[440px] sm:overflow-y-auto pr-1">
                  {filteredSavedProducts.map((p) => {
                    const isPicking = pickingId === p.id;
                    // Selection check covers both single-select
                    // (selectedSavedProductId) and multi-select
                    // (selectedSavedProductIds[]) paths.
                    const multiPosition = multiSelect
                      ? (selectedSavedProductIds?.indexOf(p.id) ?? -1)
                      : -1;
                    const isSelected = multiSelect
                      ? multiPosition >= 0
                      : selectedSavedProductId === p.id;
                    const inFlightLock = pickingId !== null && !isPicking;
                    // Route-driven disable (e.g. Fashion route disables
                    // non-fashion saved products). Distinct from the
                    // in-flight pick lock so the visual treatment +
                    // tooltip can differ.
                    const routeLocked = !!isSavedProductDisabled && isSavedProductDisabled(p);
                    const disabled = inFlightLock || routeLocked;
                    const tileTitle = routeLocked && disabledReason
                      ? disabledReason
                      : (p.name ?? 'Use this product');
                    return (
                      <button
                        key={p.id}
                        onClick={() => handleLibraryPick(p)}
                        disabled={disabled}
                        title={tileTitle}
                        className={`
                          group flex flex-col gap-1 text-left
                          ${disabled
                            ? routeLocked
                              ? 'opacity-80 cursor-not-allowed'
                              : 'opacity-50 cursor-not-allowed'
                            : 'cursor-pointer'}
                        `}
                      >
                        {/* Image tile — selected gets a primary-color ring +
                            checkmark badge; picking shows a centered spinner. */}
                        <div className={`
                          relative aspect-square rounded-xl overflow-hidden
                          border-2 transition-all bg-surface/40
                          ${isSelected
                            ? 'border-primary shadow-md shadow-primary/25 ring-2 ring-primary/30'
                            : isPicking
                              ? 'border-primary/60 shadow-md shadow-primary/20'
                              : 'border-surface-dark/30 group-hover:border-primary/60 group-hover:shadow-md group-hover:shadow-primary/15'}
                        `}>
                          {p.imageUrl ? (
                            <img
                              src={p.imageUrl}
                              alt={p.name || 'Product'}
                              className={`absolute inset-0 w-full h-full object-contain p-1.5 transition-all ${routeLocked ? 'grayscale' : ''}`}
                              draggable={false}
                            />
                          ) : (
                            <div className="absolute inset-0 flex items-center justify-center text-dark-muted/40">
                              <i className="ri-image-line text-2xl" />
                            </div>
                          )}
                          {isSelected && (
                            <div className="absolute top-1.5 right-1.5 w-5 h-5 rounded-full bg-primary text-white flex items-center justify-center shadow-md">
                              {multiSelect ? (
                                // Position badge — 1-indexed slot
                                // number so the user can see frame
                                // mapping at a glance.
                                <span className="text-[10px] font-black leading-none">
                                  {multiPosition + 1}
                                </span>
                              ) : (
                                <i className="ri-check-line text-xs" />
                              )}
                            </div>
                          )}
                          {/* Route-locked ribbon — centered "Not for X"
                              label sits on top of the (grayscaled)
                              image. Communicates WHY the tile is
                              disabled at a glance, no hover required.
                              Bg uses dark/85 + backdrop-blur so it
                              reads against any product photo color. */}
                          {routeLocked && !isSelected && (
                            <div className="absolute inset-0 flex items-center justify-center pointer-events-none px-2">
                              <span className="
                                inline-block px-2.5 py-1 rounded-full
                                bg-dark/85 backdrop-blur-sm text-white
                                text-[10px] font-bold uppercase tracking-wider
                                shadow-lg text-center leading-tight
                              ">
                                {disabledLabel}
                              </span>
                            </div>
                          )}
                          {isPicking && (
                            <div className="absolute inset-0 flex items-center justify-center bg-white/70">
                              <i className="ri-loader-4-line animate-spin text-primary text-2xl" />
                            </div>
                          )}
                        </div>
                        {/* Name below tile — bumped to text-xs (12px)
                            from the prior text-[10px] for readability;
                            still 2-line clamp. Selected state switches
                            to primary color + bold so the visual focus
                            matches the tile ring above. */}
                        <span
                          className={`
                            block text-xs leading-snug px-0.5 mt-0.5
                            ${isSelected ? 'text-primary font-bold' : 'text-dark font-semibold'}
                          `}
                          style={{
                            display: '-webkit-box',
                            WebkitLineClamp: 2,
                            WebkitBoxOrient: 'vertical',
                            overflow: 'hidden',
                          }}
                        >
                          {p.name || 'Untitled'}
                        </span>
                      </button>
                    );
                  })}
                </div>
                ) : (
                  <div className="rounded-xl border-2 border-dashed border-surface-dark/40 bg-surface/30 p-6 text-center">
                    <p className="text-xs text-dark-muted leading-relaxed">
                      No saved products match{' '}
                      <span className="font-semibold text-dark">"{productSearch}"</span>.
                    </p>
                  </div>
                )}
                {pickError && (
                  <div className="mt-3 flex items-start gap-2 px-3 py-2 rounded-xl bg-red-50 border border-red-200 text-red-700 text-xs">
                    <i className="ri-error-warning-line text-sm flex-shrink-0 mt-0.5" />
                    <span className="leading-relaxed">{pickError}</span>
                  </div>
                )}
              </>
            ) : (
              <div className="flex-1 min-h-0 rounded-xl border-2 border-dashed border-surface-dark/40 bg-surface/30 p-6 text-center flex flex-col items-center justify-center">
                <div className="w-10 h-10 rounded-xl bg-white mb-2 flex items-center justify-center text-dark-muted">
                  <i className="ri-shopping-bag-3-line text-lg" />
                </div>
                <p className="text-xs text-dark-muted leading-relaxed max-w-[240px]">
                  Save products in <strong>My Products</strong> to pick them
                  with one click here. For now, add one on the right.
                </p>
              </div>
            )}
          </div>
        )}

        {/* ============ RIGHT COLUMN — URL fetch + manual upload (existing) ============
            In library mode this wrapper IS the panel — rounded + border
            + bg + padding. Inner sub-sections (URL header, OR divider,
            preview/drop-zone) drop their own card chrome since the
            wrapper provides it. No h-full anymore — the left panel
            stopped stretching once we capped its scroll, so both
            columns now size to their natural content. In useTabs mode
            this pane is hidden when activeTab !== 'upload'. */}
        {(!useTabs || activeTab === 'upload') && (
        <div className={withLibrary
          ? 'flex flex-col gap-4 w-full rounded-2xl border-2 border-surface-dark bg-white p-5'
          : 'contents'}
        >
        {/* URL INPUT — always visible. Pasting a URL while an image is
            already uploaded triggers a re-scrape, which fires onUpload
            with the new file → parent's handleUpload wipes name/desc/
            generated state for the previous product. */}
        <div className={`w-full ${cardMaxW} ${innerCardChrome} flex-shrink-0`}>
          <div className="flex items-center gap-2 mb-3">
            <div className="w-9 h-9 rounded-lg bg-primary/10 flex items-center justify-center flex-shrink-0">
              <i className="ri-link text-primary text-base" />
            </div>
            <div>
              <h3 className="font-display font-bold text-dark text-sm leading-tight">
                {uploadedImages.length === 0
                  ? 'Paste a product page URL'
                  : 'Replace with a different URL'}
              </h3>
              <p className="text-xs text-dark-muted leading-tight mt-0.5">
                We'll fetch the image, name, and description automatically.
              </p>
            </div>
          </div>
          <div className="flex flex-col sm:flex-row gap-2">
            <input
              type="url"
              value={productUrl}
              onChange={(e) => { setProductUrl(e.target.value); setScrapeError(null); }}
              onKeyDown={(e) => { if (e.key === 'Enter' && !scraping) handleScrapeUrl(); }}
              placeholder="https://yourstore.com/products/..."
              disabled={scraping}
              className="flex-1 px-4 py-2.5 rounded-xl border-2 border-dark-muted/40 bg-white focus:border-primary focus:outline-none text-sm text-dark placeholder:text-dark-muted/60 disabled:opacity-60 disabled:cursor-not-allowed transition-colors"
            />
            <button
              onClick={handleScrapeUrl}
              disabled={scraping || !productUrl.trim()}
              className="inline-flex items-center justify-center gap-2 px-5 py-2.5 rounded-xl bg-primary hover:bg-primary-hover text-white text-sm font-bold shadow-lg shadow-primary/15 transition-colors cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap"
            >
              {scraping ? (
                <>
                  <i className="ri-loader-4-line animate-spin text-base" />
                  Fetching…
                </>
              ) : (
                <>
                  <i className="ri-magic-line text-base" />
                  {uploadedImages.length === 0 ? 'Fetch product' : 'Replace from URL'}
                </>
              )}
            </button>
          </div>
          <p className="text-[11px] text-dark-muted mt-2.5">
            Works with Shopify, Amazon, Etsy, WooCommerce, BigCommerce, and most modern store pages.
          </p>
          {scrapeError && (
            <div className="mt-3 flex items-start gap-2 px-3 py-2 rounded-xl bg-red-50 border border-red-200 text-red-700 text-xs">
              <i className="ri-error-warning-line text-sm flex-shrink-0 mt-0.5" />
              <span className="leading-relaxed">{scrapeError}</span>
            </div>
          )}
        </div>

        {/* "or" divider — copy changes based on upload state */}
        <div className={`flex items-center gap-3 w-full ${cardMaxW} flex-shrink-0`}>
          <div className="flex-1 h-px bg-surface-dark/40" />
          <span className="text-xs font-bold uppercase tracking-wider text-dark-muted">
            {uploadedImages.length === 0 ? 'or upload manually' : 'or replace from file'}
          </span>
          <div className="flex-1 h-px bg-surface-dark/40" />
        </div>

        {/* Hidden file input — used by both the drop zone and the
            "replace from file" button. Single input shared so onChange
            handler is wired once. */}
        <input
          ref={fileRef}
          type="file"
          accept="image/*"
          onChange={onInputChange}
          className="hidden"
        />

        {multiSelect ? (
          /* MULTI-PRODUCT MODE — drop zone always visible until cap;
             thumbnails strip below shows all uploaded products with
             X buttons. Used by the Carousel route (maxImages=4). */
          <>
            <div
              onClick={() => !atCap && fileRef.current?.click()}
              onDrop={(e) => { if (!atCap) onDrop(e); else { e.preventDefault(); setIsDragging(false); } }}
              onDragOver={(e) => { e.preventDefault(); if (!atCap) setIsDragging(true); }}
              onDragLeave={() => setIsDragging(false)}
              className={`rounded-2xl border-2 border-dashed p-5 text-center transition-all w-full ${cardMaxW} ${
                atCap
                  ? 'border-surface-dark/40 bg-surface/40 cursor-not-allowed opacity-70'
                  : isDragging
                    ? 'border-primary bg-primary/5 shadow-lg shadow-primary/10 cursor-pointer'
                    : 'border-surface-dark/40 bg-surface/30 hover:border-primary/40 hover:bg-surface/50 cursor-pointer'
              }`}
            >
              <div className="w-10 h-10 rounded-2xl bg-surface flex items-center justify-center mx-auto mb-2">
                <i className={`ri-upload-cloud-2-line ${atCap ? 'text-dark-muted' : 'text-primary'} text-lg`} />
              </div>
              <h3 className="font-display font-bold text-dark text-sm mb-1">
                {atCap
                  ? `Limit reached (${uploadedImages.length}/${maxImages})`
                  : `Add another product (${uploadedImages.length}/${maxImages})`}
              </h3>
              <p className="text-dark-muted text-xs">
                {atCap
                  ? 'Remove one below to add another.'
                  : 'Drag & drop, click, or paste a URL above.'}
              </p>
            </div>
            {/* Thumbnails strip — one row per uploaded product with
                position number + X button. Click X to call
                onRemoveProduct(idx). */}
            {uploadedImages.length > 0 && (
              <div className="w-full grid grid-cols-2 sm:grid-cols-4 gap-2">
                {uploadedImages.map((src, idx) => (
                  <div
                    key={`${src.slice(0, 32)}-${idx}`}
                    className="relative aspect-square rounded-xl overflow-hidden border-2 border-surface-dark/30 bg-surface/40"
                  >
                    <img
                      src={src}
                      alt={`Product ${idx + 1}`}
                      className="absolute inset-0 w-full h-full object-contain p-1.5"
                      draggable={false}
                    />
                    {/* Position badge — top-left */}
                    <div className="absolute top-1.5 left-1.5 w-5 h-5 rounded-full bg-primary text-white flex items-center justify-center shadow-md">
                      <span className="text-[10px] font-black leading-none">{idx + 1}</span>
                    </div>
                    {/* X remove button — top-right */}
                    <button
                      onClick={() => onRemoveProduct?.(idx)}
                      title={`Remove product ${idx + 1}`}
                      className="absolute top-1.5 right-1.5 w-5 h-5 rounded-full bg-dark/70 hover:bg-red-500 text-white flex items-center justify-center transition-colors cursor-pointer shadow-md"
                    >
                      <i className="ri-close-line text-[11px]" />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </>
        ) : uploadedImages.length === 0 ? (
          <>
            {/* MANUAL UPLOAD — drop zone */}
            <div
              onClick={() => fileRef.current?.click()}
              onDrop={onDrop}
              onDragOver={(e) => { e.preventDefault(); setIsDragging(true); }}
              onDragLeave={() => setIsDragging(false)}
              className={`rounded-2xl border-2 border-dashed p-6 md:p-8 text-center transition-all cursor-pointer w-full ${withLibrary ? 'flex-1 flex flex-col items-center justify-center' : 'flex-shrink-0'} ${cardMaxW} ${
                isDragging
                  ? 'border-primary bg-primary/5 shadow-lg shadow-primary/10'
                  : withLibrary
                    ? 'border-surface-dark/40 bg-surface/30 hover:border-primary/40 hover:bg-surface/50'
                    : 'border-surface-dark bg-white hover:border-primary/40 hover:bg-surface'
              }`}
            >
              <div className="w-12 h-12 rounded-2xl bg-surface flex items-center justify-center mx-auto mb-3">
                <i className="ri-upload-cloud-2-line text-primary text-xl" />
              </div>
              <h3 className="font-display font-bold text-dark text-sm mb-1">
                Drag & drop or click to upload
              </h3>
              <p className="text-dark-muted text-xs">
                JPG, PNG, WEBP up to 10MB · best with white/plain background
              </p>
            </div>

            {/* Tips — hidden in library mode (the right column is too narrow
                to fit all three side-by-side cleanly, and the saved-products
                pane on the left is the primary path anyway). */}
            {!withLibrary && (
              <div className="flex items-center justify-center gap-6 flex-shrink-0">
                {[
                  { icon: 'ri-lightbulb-line', title: 'Good Lighting' },
                  { icon: 'ri-crop-line', title: 'Centered' },
                  { icon: 'ri-contrast-line', title: 'High Contrast' },
                ].map((tip) => (
                  <div key={tip.title} className="flex items-center gap-2 text-dark-muted">
                    <i className={`${tip.icon} text-primary text-sm`} />
                    <span className="text-xs font-medium">{tip.title}</span>
                  </div>
                ))}
              </div>
            )}
          </>
        ) : compactPreview ? (
          /* COMPACT PREVIEW — slim row with a tiny thumbnail + filename +
             replace/remove actions. Used by /appv2/recreate/:id where
             the full-resolution result is already shown in the LEFT
             pane, so the upload section just needs to confirm "yes
             this is the product we're working with". */
          <div className="w-full flex items-center gap-3 p-3 rounded-xl bg-white border border-surface-dark/40">
            <div className="w-12 h-12 rounded-lg overflow-hidden border border-surface-dark/30 bg-surface/30 flex-shrink-0">
              <img
                src={uploadedImages[0]}
                alt="Product thumbnail"
                className="w-full h-full object-contain"
                draggable={false}
              />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-semibold text-dark truncate">
                Product uploaded
              </p>
              <p className="text-xs text-dark-muted truncate">
                {uploadedImages.length} of {maxImages} · click Replace to swap
              </p>
            </div>
            <button
              onClick={() => fileRef.current?.click()}
              title="Replace with a different image"
              className="px-3 py-1.5 rounded-full border border-surface-dark/40 hover:border-primary/40 hover:text-primary text-xs font-semibold text-dark transition-colors cursor-pointer whitespace-nowrap"
            >
              Replace
            </button>
            <button
              onClick={() => onUpload([], [])}
              title="Remove image"
              className="w-7 h-7 rounded-full bg-surface text-dark-muted hover:bg-red-50 hover:text-red-500 flex items-center justify-center transition-colors cursor-pointer flex-shrink-0"
            >
              <i className="ri-close-line text-sm" />
            </button>
          </div>
        ) : (
          /* PREVIEW + replace-from-file button. Card layout matches the
             URL input above for visual symmetry. */
          <div className={`w-full ${cardMaxW} ${innerPreviewChrome} flex flex-col gap-3 ${withLibrary ? 'flex-1' : 'flex-shrink-0'}`}>
            <div className="flex items-center justify-between">
              <h3 className="font-display font-bold text-dark text-sm">
                Product Preview
              </h3>
              <span className="text-xs text-dark-muted">
                {uploadedImages.length} of {maxImages} uploaded
              </span>
            </div>

            {/* Image — w-full anchors width so aspect-square resolves
                deterministically. Safari was collapsing the previous
                wrapper to 0×0 because aspect-square + max-w-lg has no
                width anchor (modern Chrome/FF resolve this via aspect-
                ratio heuristics but Safari doesn't). In library mode
                we cap to ~240px so the section doesn't grow when an
                image lands; in single-column mode we keep the original
                generous ~max-w-md / max-h-[60vh] caps. */}
            <div className={`relative group w-full ${previewMaxW} aspect-square ${previewMaxH} mx-auto`}>
              <div className="rounded-xl overflow-hidden border border-surface-dark/30 shadow-lg w-full h-full bg-surface/30 flex items-center justify-center">
                <img
                  src={uploadedImages[0]}
                  alt="Product"
                  className="w-full h-full object-contain"
                  draggable={false}
                />
              </div>
              <button
                onClick={() => onUpload([], [])}
                title="Remove image"
                className="absolute top-3 right-3 w-8 h-8 rounded-full bg-dark/60 text-white flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity cursor-pointer hover:bg-red-500"
              >
                <i className="ri-close-line" />
              </button>
            </div>

            <button
              onClick={() => fileRef.current?.click()}
              className="w-full flex items-center justify-center gap-2 px-4 py-2 rounded-xl border border-surface-dark hover:border-primary/40 text-dark-muted hover:text-dark text-sm font-medium transition-all cursor-pointer"
            >
              <i className="ri-upload-cloud-2-line" />
              Replace from file
            </button>
          </div>
        )}
        </div>
        )}
      </div>
    </div>
  );
}
